import {
  EVENT_TRUTH_GATE,
  type EventTruthPillarStage,
  type EventTruthTapeEvaluationCandidate,
  type EventTruthTapeStatusResponse,
} from "./types";

type EventTruthTapeTone = "green" | "amber" | "blue";

const STAGE_ORDER: Record<EventTruthPillarStage, number> = {
  unavailable: 0,
  accumulate: 10,
  join_wait: 20,
  first_join: 30,
  gate_cells: 40,
  gate_episodes: 50,
  gate_symbols: 55,
  gate_coverage: 60,
  gate_clocks: 65,
  gate_publisher: 70,
  gate_halves: 75,
  gate_concentration: 80,
  gate_bootstrap: 85,
  gate_economic: 90,
  pass_shadow: 100,
  falsified: 110,
};

const HOLD_REASON_STAGE: Record<string, EventTruthPillarStage> = {
  total_n_below_40: "gate_cells",
  first_half_n_below_20: "gate_halves",
  second_half_n_below_20: "gate_halves",
  episode_blocks_below_10: "gate_episodes",
  first_half_episodes_below_5: "gate_halves",
  second_half_episodes_below_5: "gate_halves",
  distinct_symbols_below_5: "gate_symbols",
  symbol_blocks_below_5: "gate_symbols",
  first_half_symbol_blocks_below_2: "gate_halves",
  second_half_symbol_blocks_below_2: "gate_halves",
  utc_day_blocks_below_5: "gate_halves",
  first_half_utc_day_blocks_below_2: "gate_halves",
  second_half_utc_day_blocks_below_2: "gate_halves",
  top_symbol_share_not_below_50pct: "gate_concentration",
  joined_coverage_below_50pct: "gate_coverage",
  internal_clock_validity_below_99pct: "gate_clocks",
  publisher_coverage_below_80pct: "gate_publisher",
  null_required_bootstrap_lower_bound: "gate_bootstrap",
};

const HOLD_REASON_LABEL_KO: Record<string, string> = {
  total_n_below_40: "게이트셀 40 미만",
  first_half_n_below_20: "전반부 표본 20 미만",
  second_half_n_below_20: "후반부 표본 20 미만",
  episode_blocks_below_10: "에피소드 10 미만",
  first_half_episodes_below_5: "전반 에피소드 5 미만",
  second_half_episodes_below_5: "후반 에피소드 5 미만",
  distinct_symbols_below_5: "심볼 5종 미만",
  symbol_blocks_below_5: "심볼 블록 5 미만",
  first_half_symbol_blocks_below_2: "전반 심볼 블록 2 미만",
  second_half_symbol_blocks_below_2: "후반 심볼 블록 2 미만",
  utc_day_blocks_below_5: "UTC일 5 미만",
  first_half_utc_day_blocks_below_2: "전반 UTC일 2 미만",
  second_half_utc_day_blocks_below_2: "후반 UTC일 2 미만",
  top_symbol_share_not_below_50pct: "최대 심볼 점유 50% 이상",
  joined_coverage_below_50pct: "조인 커버리지 50% 미만",
  internal_clock_validity_below_99pct: "내부 시계 유효 99% 미만",
  publisher_coverage_below_80pct: "출판 시각 80% 미만",
  null_required_bootstrap_lower_bound: "부트스트랩 하한 미산출",
};

const STAGE_TITLE_KO: Record<EventTruthPillarStage, string> = {
  unavailable: "정보 엣지 측정",
  accumulate: "정보 엣지 적재",
  join_wait: "정보 엣지 조인 대기",
  first_join: "정보 엣지 조인",
  gate_cells: "정보 엣지 폭 확보",
  gate_episodes: "정보 엣지 품질 게이트",
  gate_symbols: "정보 엣지 품질 게이트",
  gate_coverage: "정보 엣지 품질 게이트",
  gate_clocks: "정보 엣지 품질 게이트",
  gate_publisher: "정보 엣지 품질 게이트",
  gate_halves: "정보 엣지 품질 게이트",
  gate_concentration: "정보 엣지 품질 게이트",
  gate_bootstrap: "정보 엣지 경제 검증",
  gate_economic: "정보 엣지 경제 검증",
  pass_shadow: "정보 엣지 섀도 통과",
  falsified: "정보 엣지 기각",
};

const STATUS_LABEL_KO: Record<string, string> = {
  hold: "보류",
  pass_shadow: "섀도통과",
  falsified: "기각",
};

const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  research_dir_missing: "연구 데이터 경로 없음",
  tape_dir_missing: "마켓 테이프 미시작",
};

function kindLabelKo(kind: string | null | undefined, candidateId: string) {
  if (kind === "listing") return "상장";
  if (kind === "ofi") return "OFI";
  if (kind === "entity-graph") return "엔티티";
  if (kind) return kind;
  return candidateId;
}

function parseHoldReasonPrimary(candidate: EventTruthTapeEvaluationCandidate): string | undefined {
  if (candidate.holdReasonPrimary?.trim()) return candidate.holdReasonPrimary.trim();
  const holdReason = candidate.holdReason?.trim();
  if (!holdReason) return undefined;
  return holdReason.split(",")[0]?.trim();
}

function gateCellCount(candidate: EventTruthTapeEvaluationCandidate) {
  if (candidate.gateCells != null) return candidate.gateCells;
  return candidate.verdictCells ?? 0;
}

function resolvePillarStage(
  candidate: EventTruthTapeEvaluationCandidate,
  hasEvaluations: boolean,
): EventTruthPillarStage {
  if (candidate.status === "falsified") return "falsified";
  if (candidate.status === "pass_shadow") return "pass_shadow";
  if (!hasEvaluations) return "accumulate";

  const joined = candidate.joinedCells ?? 0;
  const gate = gateCellCount(candidate);
  if (joined === 0) return "join_wait";
  if (gate === 0) return "first_join";

  if (candidate.status === "hold") {
    const primary = parseHoldReasonPrimary(candidate);
    if (primary && HOLD_REASON_STAGE[primary]) return HOLD_REASON_STAGE[primary];
    return "gate_cells";
  }

  return "gate_economic";
}

function formatGateCellsProgress(gate: number) {
  return `게이트셀 ${gate.toLocaleString("ko-KR")}/${EVENT_TRUTH_GATE.minGateCells}`;
}

function formatStageCounts(
  status: EventTruthTapeStatusResponse,
  worst: ReturnType<typeof resolvePillarProgress>,
  pillars: ReturnType<typeof resolvePillarProgress>[],
) {
  const worstStage = worst.stage;
  const tape = status.tape;
  const tapeLine =
    tape && tape.totalLines > 0 ? `테이프 ${tape.totalLines.toLocaleString("ko-KR")}줄` : undefined;
  const maxJoined = Math.max(0, ...pillars.map((pillar) => pillar.joinedCells));
  const maxGate = Math.max(0, ...pillars.map((pillar) => pillar.gateCells));

  switch (worstStage) {
    case "accumulate":
      return [tapeLine, tape ? `심볼 ${tape.symbols.length}개` : undefined]
        .filter(Boolean)
        .join(" · ");
    case "join_wait":
      return [tapeLine, `라이브 조인 ${worst.joinedCells}건`].filter(Boolean).join(" · ");
    case "first_join":
      return [`조인 ${maxJoined}건`, formatGateCellsProgress(maxGate)].join(" · ");
    case "gate_cells":
      return [formatGateCellsProgress(maxGate), `조인 ${maxJoined}건`].join(" · ");
    case "gate_episodes": {
      const maxEpisodes = Math.max(0, ...pillars.map((pillar) => pillar.episodeBlocks));
      return [
        `에피소드 ${maxEpisodes}/${EVENT_TRUTH_GATE.minEpisodeBlocks}`,
        formatGateCellsProgress(maxGate),
      ].join(" · ");
    }
    case "gate_coverage": {
      const coverage = pillars
        .map((pillar) => pillar.joinedCoveragePct)
        .filter((value): value is number => value != null)
        .reduce((min, value) => Math.min(min, value), Number.POSITIVE_INFINITY);
      const coverageLabel =
        Number.isFinite(coverage) && coverage !== Number.POSITIVE_INFINITY
          ? `조인커버 ${Math.round(coverage)}%`
          : "조인커버 대기";
      return [coverageLabel, formatGateCellsProgress(maxGate)].join(" · ");
    }
    case "pass_shadow":
      return [`게이트셀 ≥${EVENT_TRUTH_GATE.minGateCells}`, `후보 ${pillars.length}/3 평가`].join(
        " · ",
      );
    case "falsified":
      return [tapeLine ?? "평가 완료", `후보 ${pillars.length}/3`].filter(Boolean).join(" · ");
    default:
      return [formatGateCellsProgress(maxGate), tapeLine].filter(Boolean).join(" · ");
  }
}

function resolvePillarProgress(
  candidate: EventTruthTapeEvaluationCandidate,
  kind: string | null | undefined,
  hasEvaluations: boolean,
) {
  const stage = resolvePillarStage(candidate, hasEvaluations);
  const primary = parseHoldReasonPrimary(candidate);
  const joinedCells = candidate.joinedCells ?? 0;
  const gateCells = gateCellCount(candidate);
  const label = kindLabelKo(kind, candidate.candidateId);
  const statusLabel = STATUS_LABEL_KO[candidate.status] ?? candidate.status;
  const nextGateLabelKo =
    primary && HOLD_REASON_LABEL_KO[primary]
      ? HOLD_REASON_LABEL_KO[primary]
      : stage === "join_wait"
        ? "라이브 이벤트·테이프 누적"
        : stage === "first_join"
          ? "판정셀 1건+"
          : stage === "pass_shadow"
            ? "섀도 관찰 지속"
            : "다음 게이트 대기";

  let snippet: string;
  if (candidate.status === "pass_shadow") {
    snippet = `${label} ${statusLabel}`;
  } else if (candidate.status === "falsified") {
    snippet = `${label} ${statusLabel}`;
  } else if (stage === "join_wait") {
    snippet = `${label} 조인 ${joinedCells}건`;
  } else if (stage === "first_join") {
    snippet = `${label} 조인 ${joinedCells}건 — 판정셀 대기`;
  } else if (candidate.status === "hold") {
    snippet = `${label} ${statusLabel} — ${nextGateLabelKo}`;
  } else {
    snippet = `${label} ${statusLabel}`;
  }

  return {
    stage,
    label,
    joinedCells,
    gateCells,
    episodeBlocks: candidate.episodeBlocks ?? 0,
    joinedCoveragePct: candidate.joinedCoveragePct ?? null,
    snippet,
  };
}

function pickWorstPillar(pillars: ReturnType<typeof resolvePillarProgress>[]) {
  return pillars.reduce((worst, pillar) =>
    STAGE_ORDER[pillar.stage] < STAGE_ORDER[worst.stage] ? pillar : worst,
  );
}

function formatPillarsReview(
  status: EventTruthTapeStatusResponse,
  pillars: ReturnType<typeof resolvePillarProgress>[],
) {
  const snippets = pillars.map((pillar) => pillar.snippet);
  const hasPassShadow = (status.evaluations?.candidates ?? []).some(
    (candidate) => candidate.status === "pass_shadow",
  );
  const footnote = hasPassShadow ? "라이브 승격 없음" : "관찰 전용 연구";
  return `${snippets.join(" · ")} · ${footnote}`;
}

function accumulatingTone(
  status: EventTruthTapeStatusResponse,
  worstStage: EventTruthPillarStage,
): EventTruthTapeTone {
  if (worstStage === "falsified") return "amber";
  if (worstStage === "pass_shadow") return "green";
  if ((status.evaluations?.candidates ?? []).length > 0) return "green";
  if (status.tape) return "blue";
  return "blue";
}

function unavailableReviewLabel(status?: EventTruthTapeStatusResponse): string {
  const reason = status?.reason?.trim();
  if (!reason) return "관찰 전용 연구 데이터 대기";
  const mapped = UNAVAILABLE_REASON_LABELS[reason];
  if (mapped) return mapped;
  if (reason.includes("_")) return reason.replace(/_/g, " ");
  return "관찰 전용 연구 데이터 대기";
}

export function summarizeEventTruthTapeOperatorLine(
  status?: EventTruthTapeStatusResponse,
  options?: { failed?: boolean },
): {
  tone: EventTruthTapeTone;
  title: string;
  counts: string;
  review: string;
} {
  if (!status || options?.failed || !status.available) {
    return {
      tone: "amber",
      title: "정보 엣지 측정",
      counts: "데이터 없음",
      review: unavailableReviewLabel(status),
    };
  }

  const evaluations = status.evaluations?.candidates ?? [];
  const hasEvaluations = evaluations.length > 0;
  const kindById = new Map(
    (status.registry?.candidates ?? []).map((candidate) => [candidate.candidateId, candidate.kind]),
  );

  if (!hasEvaluations) {
    const tape = status.tape;
    return {
      tone: "blue",
      title: "정보 엣지 적재",
      counts: tape
        ? `테이프 ${tape.totalLines.toLocaleString("ko-KR")}줄 · 심볼 ${tape.symbols.length}개`
        : "테이프 대기",
      review: `후보 ${status.registry?.candidateCount ?? 0}개 등록 · 일일 평가 대기 · 관찰 전용 연구`,
    };
  }

  const pillars = evaluations.map((candidate) =>
    resolvePillarProgress(candidate, kindById.get(candidate.candidateId), hasEvaluations),
  );
  const worst = pickWorstPillar(pillars);

  return {
    tone: accumulatingTone(status, worst.stage),
    title: STAGE_TITLE_KO[worst.stage],
    counts: formatStageCounts(status, worst, pillars),
    review: formatPillarsReview(status, pillars),
  };
}
